import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Lock, Unlock } from "lucide-react";
import { useGoogleMaps } from "@/lib/useGoogleMaps";
import { cn } from "@/lib/cn";

/**
 * A single plotted point on the map. Deliberately generic so the SAME map
 * serves the Board (day-of dispatches) AND the SEG-01 route leg (agreement
 * members) — both just hand it points. (BOARD-MAP-01 / ROUTE.)
 */
/** Rich content shown when the dispatcher hovers a pin (BOARD-MAP-HOVER-01). */
export interface MapPointHover {
  title: string; // customer / member name
  chips?: { text: string; className?: string }[]; // window / priority / tags
  meta?: string; // e.g. "10:30 AM · Eagan"
  note?: string; // notes preview
}

export interface MapPoint {
  id: string; // dispatch number, or member key — whatever the caller keys on
  lat: number;
  lng: number;
  label?: string; // primary line (customer / member name)
  sublabel?: string; // secondary line (time window / status / city)
  colorKey?: string; // grouping key → stable color (e.g. tech empno)
  colorHex?: string; // explicit color (wins over colorKey)
  order?: number; // 1-based stop sequence (drives the marker number + route line)
  hover?: MapPointHover; // richer hover-card content (optional)
  /**
   * "waypoint" = a place the tech goes that ISN'T a customer call (meeting,
   * errand, training). Drawn as a diamond, never numbered, and excluded from
   * the straight pin-to-pin route lines (the road path handles routing).
   */
  shape?: "stop" | "waypoint";
}

interface DispatchMapProps {
  points: MapPoint[];
  selectedId?: string | null;
  onSelectPoint?: (id: string) => void;
  /** Optional caller-supplied color map for `colorKey` (e.g. a per-tech palette). */
  colorForKey?: (key: string) => string;
  /** Draw a connecting route line per color group, through points in `order`. */
  showRoutes?: boolean;
  /**
   * Actual road-following path (a decoded polyline) for the visible route.
   * When provided alongside `showRoutes`, it's drawn INSTEAD of the straight
   * pin-to-pin lines — the real drive path. (ROUTE-REAL-02)
   */
  routePath?: { lat: number; lng: number }[] | null;
  className?: string;
  /**
   * When set, the map remembers its center/zoom + lock state under this key
   * (per browser). Lock freezes the view so it stops auto-fitting as you click
   * around; unlocking re-fits to all stops.
   */
  viewStorageKey?: string;
  /**
   * PLANNER-02 B — freehand loop drawing. While `drawMode` is on, dragging on
   * the map draws a loop instead of panning; on release `onDrawEnd` gets the
   * closed path in lat/lng. `loops` are saved loops to show (a polygon each).
   */
  drawMode?: boolean;
  onDrawEnd?: (path: { lat: number; lng: number }[]) => void;
  loops?: { id: string; path: { lat: number; lng: number }[]; color?: string; selected?: boolean }[];
  onSelectLoop?: (id: string) => void;
}

// Shared deterministic palette. Exported so callers (e.g. the Board legend) can
// assign the SAME colors to the SAME keys their pins use.
export const MAP_PALETTE = [
  "#2563eb",
  "#dc2626",
  "#059669",
  "#d97706",
  "#7c3aed",
  "#0891b2",
  "#db2777",
  "#65a30d",
  "#ea580c",
  "#4f46e5",
];

function hashColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return MAP_PALETTE[h % MAP_PALETTE.length];
}

// Loosely-typed (no @types/google.maps — see useGoogleMaps note).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function markerIcon(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  g: any,
  color: string,
  selected: boolean,
  numbered: boolean,
  waypoint: boolean,
) {
  if (waypoint) {
    // Diamond — visually "a place the tech goes, not a customer call".
    return {
      path: "M 0,-1 1,0 0,1 -1,0 z",
      scale: selected ? 12 : 9,
      fillColor: color,
      fillOpacity: 0.95,
      strokeColor: selected ? "#0f172a" : "#ffffff",
      strokeWeight: selected ? 3 : 1.5,
      labelOrigin: new g.maps.Point(0, 0),
    };
  }
  // Numbered markers are larger so the sequence digit fits inside.
  const base = numbered ? 11 : 7;
  return {
    path: g.maps.SymbolPath.CIRCLE,
    scale: selected ? base + 3 : base,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: selected ? "#0f172a" : "#ffffff",
    strokeWeight: selected ? 3 : 1.5,
    labelOrigin: new g.maps.Point(0, 0),
  };
}

// --- View persistence (center / zoom / lock) ---
type SavedView = { lat: number; lng: number; zoom: number };

function readView(key: string): SavedView | null {
  try {
    const raw = localStorage.getItem(`${key}.view`);
    if (!raw) return null;
    const v = JSON.parse(raw);
    // Design-review Wave-1 (the blank-Map bug): NaN passes `typeof === "number"`,
    // so ONE corrupt persisted view (NaN center) painted the Map view as a blank
    // beige canvas forever — and the idle listener kept re-saving it. Validate
    // with real bounds and SELF-HEAL by discarding a corrupt view (the map then
    // falls back to the Twin Cities default).
    if (
      Number.isFinite(v?.lat) && Number.isFinite(v?.lng) && Number.isFinite(v?.zoom) &&
      v.lat >= -85 && v.lat <= 85 && v.lng >= -180 && v.lng <= 180 && v.zoom >= 2 && v.zoom <= 22
    )
      return v as SavedView;
    localStorage.removeItem(`${key}.view`);
  } catch {
    // ignore
  }
  return null;
}

function writeView(key: string, v: SavedView) {
  // Never persist a non-finite view — that's how the blank-map corruption began.
  if (!Number.isFinite(v.lat) || !Number.isFinite(v.lng) || !Number.isFinite(v.zoom)) return;
  try {
    localStorage.setItem(`${key}.view`, JSON.stringify(v));
  } catch {
    // ignore
  }
}

function readLocked(key: string): boolean {
  try {
    return localStorage.getItem(`${key}.locked`) === "1";
  } catch {
    return false;
  }
}

function writeLocked(key: string, locked: boolean) {
  try {
    localStorage.setItem(`${key}.locked`, locked ? "1" : "0");
  } catch {
    // ignore
  }
}

/**
 * Reusable Google map of dispatch/member points. Colors markers by `colorKey`,
 * highlights the selected point, reports clicks via `onSelectPoint`, and (when
 * `viewStorageKey` is set) remembers its framing + a lock toggle so it doesn't
 * fly around as you work. Renders an in-canvas hint when the SDK can't load.
 */
export function DispatchMap({
  points,
  selectedId,
  onSelectPoint,
  colorForKey,
  showRoutes,
  routePath,
  className,
  viewStorageKey,
  drawMode,
  onDrawEnd,
  loops,
  onSelectLoop,
}: DispatchMapProps) {
  const { state, ready } = useGoogleMaps();
  const divRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markersRef = useRef<Map<string, any>>(new Map());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const polylinesRef = useRef<any[]>([]);
  const onSelectRef = useRef(onSelectPoint);
  onSelectRef.current = onSelectPoint;
  // Read through a ref so an inline `colorForKey` never re-syncs every marker
  // on each render (1,269 pins × every pointer move = a frozen tab).
  const colorForKeyRef = useRef(colorForKey);
  colorForKeyRef.current = colorForKey;
  const lastFitSigRef = useRef<string>("");
  // Current points keyed by id, so a reused marker's hover listener reads the
  // LATEST content (not what it had at creation).
  const pointByIdRef = useRef(new Map<string, MapPoint>());
  const hoverOutTimer = useRef<number | undefined>(undefined);

  const [locked, setLocked] = useState<boolean>(() =>
    viewStorageKey ? readLocked(viewStorageKey) : false,
  );
  const [hover, setHover] = useState<{
    point: MapPoint;
    x: number;
    y: number;
  } | null>(null);

  // ---- PLANNER-02 B: freehand loop drawing over the map ----
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overlayRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loopShapesRef = useRef<any[]>([]);
  const [drawPts, setDrawPts] = useState<{ x: number; y: number }[] | null>(null);
  const onDrawEndRef = useRef(onDrawEnd);
  onDrawEndRef.current = onDrawEnd;
  const onSelectLoopRef = useRef(onSelectLoop);
  onSelectLoopRef.current = onSelectLoop;

  // Create the map once the SDK is ready (restoring a saved view if present).
  useEffect(() => {
    if (!ready || !divRef.current || mapRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (window as any).google;
    if (!g?.maps?.Map) return; // defensive: never call a missing constructor

    const saved = viewStorageKey ? readView(viewStorageKey) : null;
    mapRef.current = new g.maps.Map(divRef.current, {
      center: saved
        ? { lat: saved.lat, lng: saved.lng }
        : { lat: 44.95, lng: -93.3 }, // Twin Cities default
      zoom: saved ? saved.zoom : 11,
      disableDefaultUI: true,
      zoomControl: true,
      clickableIcons: false,
      gestureHandling: "greedy",
      // Design-review Wave-1 (the blank-Map bug, root cause): Google defaulted
      // this map to VECTOR (WebGL) rendering, and the office's server-grade
      // machine has no GPU for it — 21 blank canvases, beige void. RASTER uses
      // classic image tiles and paints everywhere. (The probe: .gm-style full of
      // <canvas>, every <img> = transparent.png.)
      renderingType: g.maps.RenderingType?.RASTER ?? "RASTER",
    });

    // Don't auto-fit over a restored view (avoids a jump on first paint).
    if (saved) lastFitSigRef.current = "__restored__";

    // A silent overlay whose projection turns container pixels into lat/lng
    // (the loop tool needs it; OverlayView is core Maps — no extra library).
    try {
      const ov = new g.maps.OverlayView();
      ov.onAdd = () => undefined;
      ov.draw = () => undefined;
      ov.onRemove = () => undefined;
      ov.setMap(mapRef.current);
      overlayRef.current = ov;
    } catch {
      overlayRef.current = null;
    }

    // Remember the dispatcher's framing as they pan/zoom.
    if (viewStorageKey) {
      g.maps.event.addListener(mapRef.current, "idle", () => {
        const c = mapRef.current?.getCenter?.();
        const z = mapRef.current?.getZoom?.();
        if (c && typeof z === "number") {
          writeView(viewStorageKey, { lat: c.lat(), lng: c.lng(), zoom: z });
        }
      });
    }
  }, [ready, viewStorageKey]);

  // Google Maps doesn't repaint when its container changes size (e.g. inside a
  // flex pane like the Cockpit view). Observe the container and nudge a resize.
  useEffect(() => {
    if (!ready || !divRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (window as any).google;
    const el = divRef.current;
    const ro = new ResizeObserver(() => {
      const map = mapRef.current;
      if (map && g?.maps?.event) {
        const center = map.getCenter?.();
        g.maps.event.trigger(map, "resize");
        if (center) map.setCenter(center);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ready]);

  // Sync markers whenever the points / selection change. (No fitting here — see
  // the fit effect — so selecting a stop never makes the map fly around.)
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (window as any).google;
    const map = mapRef.current;
    const existing = markersRef.current;
    pointByIdRef.current = new Map(points.map((p) => [p.id, p]));

    const nextIds = new Set(points.map((p) => p.id));
    for (const [id, m] of existing) {
      if (!nextIds.has(id)) {
        m.setMap(null);
        existing.delete(id);
      }
    }

    for (const p of points) {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
      const color =
        p.colorHex ??
        (p.colorKey
          ? (colorForKeyRef.current?.(p.colorKey) ?? hashColor(p.colorKey))
          : "#2563eb");
      const title = [p.label, p.sublabel].filter(Boolean).join(" · ");

      let m = existing.get(p.id);
      if (!m) {
        m = new g.maps.Marker({
          map,
          position: { lat: p.lat, lng: p.lng },
          title,
        });
        m.addListener("click", () => onSelectRef.current?.(p.id));
        // Rich hover card — reads the latest point content via the ref.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        m.addListener("mouseover", (e: any) => {
          const cur = pointByIdRef.current.get(p.id);
          if (!cur?.hover) return;
          if (hoverOutTimer.current) window.clearTimeout(hoverOutTimer.current);
          const rect = divRef.current?.getBoundingClientRect();
          const dom = e?.domEvent as MouseEvent | undefined;
          setHover({
            point: cur,
            x: (dom?.clientX ?? 0) - (rect?.left ?? 0),
            y: (dom?.clientY ?? 0) - (rect?.top ?? 0),
          });
        });
        m.addListener("mouseout", () => {
          hoverOutTimer.current = window.setTimeout(() => setHover(null), 90);
        });
        existing.set(p.id, m);
      } else {
        m.setPosition({ lat: p.lat, lng: p.lng });
        m.setTitle(title);
      }
      const isSel = p.id === selectedId;
      const isWaypoint = p.shape === "waypoint";
      const numbered = typeof p.order === "number" && !isWaypoint;
      m.setIcon(markerIcon(g, color, isSel, numbered, isWaypoint));
      m.setLabel(
        numbered
          ? {
              text: String(p.order),
              color: "#ffffff",
              fontSize: "10px",
              fontWeight: "700",
            }
          : null,
      );
      m.setZIndex(isSel ? 999 : 1);
    }
  }, [ready, points, selectedId]);

  // Draw a connecting route line per color group (tech), through points in
  // `order` — so the run sequence is visible. Cleared + redrawn on change.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (window as any).google;
    for (const pl of polylinesRef.current) pl.setMap(null);
    polylinesRef.current = [];
    if (!showRoutes) return;

    if (routePath && routePath.length > 1) {
      // The ACTUAL road-following path (Routes API), colored by the visible group.
      const first = points.find(
        (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng),
      );
      const color =
        first?.colorHex ??
        (first?.colorKey
          ? (colorForKeyRef.current?.(first.colorKey) ?? hashColor(first.colorKey))
          : "#2563eb");
      const pl = new g.maps.Polyline({
        path: routePath,
        map: mapRef.current,
        strokeColor: color,
        strokeOpacity: 0.8,
        strokeWeight: 4,
      });
      polylinesRef.current.push(pl);
    } else {
      const groups = new Map<string, MapPoint[]>();
      for (const p of points) {
        if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
        if (p.shape === "waypoint") continue; // road path handles waypoint routing
        const key = p.colorKey ?? "_";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(p);
      }
      for (const [key, pts] of groups) {
        const ordered = pts
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        if (ordered.length < 2) continue;
        const color =
          ordered[0].colorHex ??
          (key !== "_" ? (colorForKeyRef.current?.(key) ?? hashColor(key)) : "#2563eb");
        const pl = new g.maps.Polyline({
          path: ordered.map((p) => ({ lat: p.lat, lng: p.lng })),
          map: mapRef.current,
          strokeColor: color,
          strokeOpacity: 0.7,
          strokeWeight: 3,
        });
        polylinesRef.current.push(pl);
      }
    }

    return () => {
      for (const pl of polylinesRef.current) pl.setMap(null);
      polylinesRef.current = [];
    };
  }, [ready, points, showRoutes, routePath]);

  // PLANNER-02 B — saved loops as polygons.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (window as any).google;
    for (const sh of loopShapesRef.current) sh.setMap(null);
    loopShapesRef.current = [];
    for (const lp of loops ?? []) {
      if (lp.path.length < 3) continue;
      const color = lp.color ?? "#1d4ed8";
      const poly = new g.maps.Polygon({
        paths: lp.path,
        map: mapRef.current,
        strokeColor: color,
        strokeOpacity: lp.selected ? 1 : 0.7,
        strokeWeight: lp.selected ? 3 : 2,
        fillColor: color,
        fillOpacity: lp.selected ? 0.14 : 0.07,
        clickable: Boolean(onSelectLoopRef.current),
        zIndex: lp.selected ? 2 : 1,
      });
      poly.addListener("click", () => onSelectLoopRef.current?.(lp.id));
      loopShapesRef.current.push(poly);
    }
    return () => {
      for (const sh of loopShapesRef.current) sh.setMap(null);
      loopShapesRef.current = [];
    };
  }, [ready, loops]);

  // While drawing, the map must not pan under the pointer.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    mapRef.current.setOptions({ gestureHandling: drawMode ? "none" : "greedy", draggableCursor: drawMode ? "crosshair" : undefined });
  }, [ready, drawMode]);

  const drawPtsRef = useRef<{ x: number; y: number }[] | null>(null);
  const drawStart = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drawMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* synthetic or already captured */ }
    const first = [{ x: e.clientX - rect.left, y: e.clientY - rect.top }];
    drawPtsRef.current = first;
    setDrawPts(first);
  };
  const drawMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drawMode || !drawPtsRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const p = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const cur = drawPtsRef.current;
    const last = cur[cur.length - 1];
    if (Math.hypot(p.x - last.x, p.y - last.y) < 4) return;
    const next = [...cur, p];
    drawPtsRef.current = next; // events can arrive faster than renders
    setDrawPts(next);
  };
  const drawFinish = () => {
    if (!drawMode || !drawPtsRef.current) return;
    const pts = drawPtsRef.current;
    drawPtsRef.current = null;
    setDrawPts(null);
    if (pts.length < 8) return; // a click, not a loop
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (window as any).google;
    const proj = overlayRef.current?.getProjection?.();
    if (!proj || !g?.maps?.Point) return;
    const step = Math.max(1, Math.floor(pts.length / 80));
    const path: { lat: number; lng: number }[] = [];
    for (let i = 0; i < pts.length; i += step) {
      const ll = proj.fromContainerPixelToLatLng(new g.maps.Point(pts[i].x, pts[i].y));
      if (ll) path.push({ lat: ll.lat(), lng: ll.lng() });
    }
    if (path.length >= 3) onDrawEndRef.current?.(path);
  };

  // Fit the view to the stops — only when the SET of stops changes (not on
  // selection) and only when not locked. This is what stops the "flying around."
  useEffect(() => {
    if (!ready || !mapRef.current || locked) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (window as any).google;
    const sig = points
      .map((p) => p.id)
      .sort()
      .join(",");
    if (sig === lastFitSigRef.current) return;
    lastFitSigRef.current = sig;

    const bounds = new g.maps.LatLngBounds();
    let plotted = 0;
    for (const p of points) {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
      plotted++;
      bounds.extend({ lat: p.lat, lng: p.lng });
    }
    if (plotted === 1) {
      mapRef.current.setCenter(bounds.getCenter());
      mapRef.current.setZoom(14);
    } else if (plotted > 1) {
      mapRef.current.fitBounds(bounds, 64);
    }
  }, [ready, points, locked]);

  const toggleLock = () => {
    setLocked((prev) => {
      const next = !prev;
      if (viewStorageKey) writeLocked(viewStorageKey, next);
      // Unlocking → re-fit to all stops on the next render.
      if (!next) lastFitSigRef.current = "";
      return next;
    });
  };

  return (
    <div
      className={cn(
        "relative h-full w-full overflow-hidden rounded-xl border border-hairline bg-slate-100",
        className,
      )}
    >
      <div ref={divRef} className="h-full w-full" />

      {drawMode && (
        <div
          className="absolute inset-0 z-10 cursor-crosshair touch-none"
          onPointerDown={drawStart}
          onPointerMove={drawMove}
          onPointerUp={drawFinish}
          onPointerCancel={() => { drawPtsRef.current = null; setDrawPts(null); }}
        >
          {drawPts && drawPts.length > 1 && (
            <svg className="pointer-events-none absolute inset-0 h-full w-full">
              <polyline points={drawPts.map((p) => `${p.x},${p.y}`).join(" ")} fill="rgba(29,78,216,0.10)" stroke="#1d4ed8" strokeWidth={2.5} strokeDasharray="7 5" strokeLinejoin="round" />
            </svg>
          )}
          {!drawPts && (
            <div className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white shadow-e2">
              Draw a loop around the houses you want — release to finish
            </div>
          )}
        </div>
      )}

      {hover?.point.hover &&
        (() => {
          const cw = divRef.current?.clientWidth ?? 600;
          const left = Math.max(118, Math.min(hover.x, cw - 118));
          const h = hover.point.hover;
          return (
            <div
              className="pointer-events-none absolute z-20 w-56"
              style={{
                left,
                top: hover.y,
                transform: "translate(-50%, calc(-100% - 14px))",
              }}
            >
              <div className="rounded-lg border border-hairline bg-white/98 p-2.5 shadow-e3 backdrop-blur">
                <div className="truncate text-xs font-semibold text-slate-900">
                  {h.title}
                </div>
                {h.chips && h.chips.length > 0 && (
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {h.chips.map((c, i) => (
                      <span
                        key={i}
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide",
                          c.className ?? "bg-slate-100 text-slate-600",
                        )}
                      >
                        {c.text}
                      </span>
                    ))}
                  </div>
                )}
                {h.meta && (
                  <div className="mt-1 text-[11px] text-slate-500">{h.meta}</div>
                )}
                {h.note && (
                  <div className="mt-1 line-clamp-2 text-[11px] leading-snug text-slate-600">
                    {h.note}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

      {viewStorageKey && ready && (
        <button
          type="button"
          onClick={toggleLock}
          title={
            locked
              ? "View locked — click to auto-fit to all stops"
              : "Lock this view so it stops re-centering"
          }
          className={cn(
            "absolute right-2 top-2 z-10 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium shadow-e1 transition-colors",
            locked
              ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
              : "border-hairline bg-white/95 text-slate-600 hover:bg-white",
          )}
        >
          {locked ? (
            <Lock className="h-3.5 w-3.5" />
          ) : (
            <Unlock className="h-3.5 w-3.5" />
          )}
          {locked ? "Locked" : "Lock view"}
        </button>
      )}

      {state !== "ready" && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-50/80 px-6 text-center text-xs text-slate-500">
          {(state === "loading" || state === "idle") && "Loading map…"}
          {state === "no-key" &&
            "Map unavailable — VITE_GOOGLE_MAPS_KEY is not set."}
          {state === "error" &&
            "Map couldn't load. Confirm the Maps JavaScript API is enabled for this key and this site is an allowed referrer."}
        </div>
      )}
    </div>
  );
}
